<template>
  <div>
    <range-modal v-model="modal" :title="$t('range') | capitalize" :item="range" @save="onSave" />
    <confirmation-modal v-model="confirmation" :item="range" @accept="deleteRange" :title="$t('confirmation')"
      :message="$t('confirm_delete')" />
    <base-loading :visible="isLoading" />
    <div class="grid grid-cols-12 gap-4">
      <div class="col-span-full md:col-span-2 md:col-start-1  xs:col-start-1">
        <vs-button @click="onAdd" color="success" icon="add"> {{ $t('add') | capitalize }}</vs-button>
      </div>
      <div class="col-span-full md:col-span-2 md:col-start-11  xs:col-start-1">
        <search-input v-model="search" @search="onSearch" @clear="onSearch" />
      </div>
      <div class="col-span-full col-start-1">
        <ranges-table :items="items" @loadMore="loadMore" @sortChanged="onSortChanged" @edit="onEdit"
          @delete="onDelete" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useNotifier } from '@/composables/useNotifier.js'
import { useNavigator } from '@/composables/useNavigator.js'
import { useRanges } from '../../composables/useRanges.js'
import RangesTable from './components/RangesTable.vue'
import RangeModal from './components/RangeModal.vue'
import ConfirmationModal from '@/views/app/components/ConfirmationModal.vue'
import BaseLoading from '@/views/app/components/BaseLoading.vue'
import SearchInput from '@/views/app/components/SearchInput.vue'

const { notify } = useNotifier()
const { getParam } = useNavigator()

const search = ref('')
const ranges = useRanges()
const range = ref({})
const confirmation = ref(false)
const modal = ref(false)
const sort = ref({})

const items = computed(() => ranges.state.items)
const currentPage = computed(() => ranges.state.currentPage)
const lastPage = computed(() => ranges.state.lastPage)
const isLoading = computed(() => ranges.state.loading)
const client = getParam('client')

const loadData = async (params = {}) => {
  const payload = {
    client: client.value,
    page: ranges.state.currentPage || 1,
    search: search.value,
    column: sort.value.column,
    order: sort.value.order,
    ...params
  }
  const result = await ranges.getRanges(payload)
  if (!result.success) notify({ title: 'error', text: 'message_error', color: 'danger' })
}

const loadMore = async () => {
  if (currentPage.value >= lastPage.value) return
  const newPage = ranges.state.currentPage + 1
  await loadData({ page: newPage })
}

const onSearch = async () => {
  await loadData({ page: 1, search: search.value })
}

const onSortChanged = async (sortData) => {
  sort.value.column = sortData.key
  sort.value.order = sortData.order
  await loadData({ column: sort.value.column, order: sort.value.order , page:1})
}

const onEdit = (row) => {
  range.value = { ...row }
  modal.value = true
}

const onDelete = (row) => {
  confirmation.value = true
  range.value = { ...row }
}

const deleteRange = async (item) => {
  const payload = { id: item.id }
  const result = await ranges.deleteRange(payload)
  confirmation.value = false
  if (!result.success) {
    notify({ title: 'attention', text: 'message_error', color: 'warning' })
    return
  }

  const rangeIndex = ranges.state.items.findIndex(c => c.id === item.id)
  if (rangeIndex !== -1) {
    ranges.state.items.splice(rangeIndex, 1)
  }

  notify({ title: 'success', text: 'message_delete', color: 'success' })
}

const onAdd = () => {
  range.value = {
    id: '',
    start_hour: '',
    end_hour: '',
    pallet_price: '',
    box_price: '',
    bulk_price: ''
  }

  modal.value = true
}

const onSave = async (data) => {

  const payload = {
    ...(data.id !== '' && { id: data.id }),
    client_id: client.value,
    start_hour: data.startHour,
    end_hour: data.endHour,
    bulk_price: data.bulkPrice,
    pallet_price: data.palletPrice,
    box_price: data.boxPrice
  }
  if (payload.id) {
    updateRange(payload)
  } else {
    addRange(payload)
  }
}

const updateRange = async (payload) => {
  const result = await ranges.updateRange(payload)
  if (!result.success) {
    notify({ title: 'attention', text: 'message_error', color: 'warning' })
    return
  }
  modal.value = false
  const rangeIndex = ranges.state.items.findIndex(r => r.id === payload.id)
  if (rangeIndex !== -1) {
    ranges.state.items[rangeIndex].start_hour = payload.start_hour
    ranges.state.items[rangeIndex].end_hour = payload.end_hour
    ranges.state.items[rangeIndex].bulk_price = payload.bulk_price
    ranges.state.items[rangeIndex].pallet_price = payload.pallet_price
    ranges.state.items[rangeIndex].box_price = payload.box_price
  }
  notify({ title: 'success', text: 'message_update', color: 'success' })
}

const addRange = async (payload) => {
  const result = await ranges.addRange(payload)
  if (!result.success) {
    notify({ title: 'attention', text: 'message_error', color: 'warning' })
    return
  }
  modal.value = false
  notify({ title: 'success', text: 'message_save', color: 'succcess' })
  
}

onMounted(() => loadData())

</script>
